import React, { useEffect, useRef, useState } from 'react';

// A film that fills the screen behind a product page and stays put while the page scrolls over it (Matt: it never
// stops playing while the page is open). The tall cut plays on portrait screens, the wide cut everywhere else; the
// film itself is cut as a seamless loop. If the browser pauses it (another tab, a phone's power saving, a stalled
// network), it is started again as soon as that is allowed.
const PORTRAIT = '(max-aspect-ratio: 4/5)';

function useTall() {
  const [tall, setTall] = useState(() => typeof matchMedia === 'function' && matchMedia(PORTRAIT).matches);
  useEffect(() => {
    if (typeof matchMedia !== 'function') return undefined;
    const query = matchMedia(PORTRAIT), change = () => setTall(query.matches);
    query.addEventListener?.('change', change);
    return () => query.removeEventListener?.('change', change);
  }, []);
  return tall;
}

export default function BackgroundFilm({ film }) {
  const video = useRef(null);
  const tall = useTall();
  const src = (tall ? film.video_tall : film.video) || film.video || film.video_tall;
  const poster = (tall ? film.poster_tall : film.poster) || film.poster || film.poster_tall || undefined;
  const [playing, setPlaying] = useState(false);

  useEffect(() => {
    const element = video.current;
    if (!element) return undefined;
    let leaving = false;
    const play = () => { if (!leaving && element.paused && document.visibilityState !== 'hidden') element.play()?.catch(() => {}); };
    // Some phones refuse to start a film until the person touches the page; the first touch or scroll starts it.
    const nudge = () => play();
    const onVisible = () => { if (document.visibilityState === 'visible') play(); };
    const onPause = () => setTimeout(play, 0);
    const onEnded = () => { element.currentTime = 0; play(); };
    const onPlaying = () => setPlaying(true);
    element.addEventListener('pause', onPause);
    element.addEventListener('ended', onEnded);
    element.addEventListener('playing', onPlaying);
    element.addEventListener('stalled', play);
    document.addEventListener('visibilitychange', onVisible);
    const onHide = () => { leaving = true; };
    const onShow = () => { leaving = false; play(); };   // back from the browser's page cache
    window.addEventListener('pagehide', onHide);
    window.addEventListener('pageshow', onShow);
    for (const name of ['touchstart', 'pointerdown', 'scroll', 'keydown']) window.addEventListener(name, nudge, { passive: true });
    // A last safety net: every few seconds, a film that should be playing but is not is started again.
    const watchdog = setInterval(play, 3000);
    play();
    return () => {
      leaving = true;
      clearInterval(watchdog);
      element.removeEventListener('pause', onPause);
      element.removeEventListener('ended', onEnded);
      element.removeEventListener('playing', onPlaying);
      element.removeEventListener('stalled', play);
      document.removeEventListener('visibilitychange', onVisible);
      window.removeEventListener('pagehide', onHide);
      window.removeEventListener('pageshow', onShow);
      for (const name of ['touchstart', 'pointerdown', 'scroll', 'keydown']) window.removeEventListener(name, nudge);
    };
  }, [src]);

  if (!src) return null;
  return <div className={`shop-bgfilm ${playing ? 'is-playing' : ''}`} aria-hidden="true">
    <video ref={video} key={src} src={src} poster={poster} autoPlay muted loop playsInline preload="auto" disablePictureInPicture disableRemotePlayback tabIndex={-1} />
  </div>;
}
